import React from 'react';
import Select from 'react-select';
import { selectStyles } from './AdminLigas';

// Panel de metricas: directos (antes la hoja "Juegos v2") y videos (antes "Series V3").
//
// Las puntuaciones las calcula el servidor (apps/streamMetrics.js, apps/videoMetrics.js)
// porque dependen de toda la tabla; aqui solo se pintan, se importa el historico y se
// corrigen a mano los campos de los directos que no salen de ninguna API.

const fmt = (v, digits = 2) => (v === null || v === undefined || v === '')
    ? ''
    : Number(v).toLocaleString('es-ES', { maximumFractionDigits: digits });

const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })
    : '';

// Los videos van por dia, sin hora: se pintan como en la hoja (AAAA-MM-DD).
const fmtDay = (iso) => (iso ? String(iso).slice(0, 10) : '');

// Cada fila lleva muchas celdas (y los directos ~10 inputs): pintarlas todas atasca el navegador.
const PAGE_SIZE = 25;

const NOTE_STYLE = { fontSize: 13, color: '#ccc', fontFamily: 'Arial, Helvetica, sans-serif' };

const post = async (url, fields) => {
    const formData = new FormData();
    Object.keys(fields).forEach(key => formData.append(key, fields[key]));
    const res = await fetch(url, { method: 'POST', body: formData });
    return res.json();
};

/** Lee /api/adminLogin?action=get&type=<type>; data es null mientras carga. */
const useMetrics = (type) => {
    const [data, setData] = React.useState(null);
    const [error, setError] = React.useState('');
    const refresh = React.useCallback(async () => {
        try {
            setData(await post('/api/adminLogin?action=get&type=' + type, {}));
        } catch (err) {
            setError('No se pudieron leer las métricas');
        }
    }, [type]);
    React.useEffect(() => { refresh(); }, [refresh]);
    return [data, error, refresh];
};

const Tabs = ({ tabs, value, onChange }) => (
    <div className='navTops heroes unselectable'>
        {tabs.map(t => (
            <div key={t.id} className={value === t.id ? 'active' : ''} onClick={() => onChange(t.id)}>
                {t.label}
            </div>
        ))}
    </div>
);

/**
 * Pagina una lista ya cargada. Para volver a la primera pagina al cambiar de filtro se le
 * cambia la key. Acotada: al borrar la ultima fila de la ultima pagina, esa pagina desaparece.
 */
const Paged = ({ rows, noun, children }) => {
    const [page, setPage] = React.useState(0);
    const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const current = Math.min(page, pages - 1);
    const pager = pages > 1 ? (
        <div className='metricsPager'>
            <div className='miniBtn' onClick={() => setPage(Math.max(0, current - 1))}>‹ Anterior</div>
            <span>Página {current + 1} de {pages} · {rows.length} {noun}</span>
            <div className='miniBtn' onClick={() => setPage(Math.min(pages - 1, current + 1))}>Siguiente ›</div>
        </div>
    ) : null;
    return <>{pager}{children(rows.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE))}{pager}</>;
};

/** Sube la exportacion CSV de una hoja; el servidor sustituye lo importado antes. */
const ImportCsv = ({ type, sheet, extra, onDone }) => {
    const [status, setStatus] = React.useState(null); // { text, error }
    const handleImport = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!window.confirm('Se sustituyen todas las filas importadas antes de un CSV. ¿Seguir?')) return;
        setStatus({ text: 'Importando...' });
        try {
            const res = await post('/api/adminLogin?action=create&type=' + type, { csv: await file.text() });
            setStatus(res.success
                ? { text: res.rows + ' filas importadas.' }
                : { text: res.error || 'No se pudo importar el CSV', error: true });
            await onDone();
        } catch (err) {
            setStatus({ text: 'No se pudo importar el CSV', error: true });
        }
    };
    return (
        <>
            <p style={NOTE_STYLE}>Exportación CSV de la hoja "{sheet}". Sustituye lo importado antes{extra}.</p>
            <input type='file' accept='.csv,text/csv' onChange={handleImport} />
            {status ? (
                <p
                    role={status.error ? 'alert' : 'status'}
                    style={{ padding: '8px 12px', borderRadius: 4, fontWeight: 600, color: status.error ? '#8a1a1a' : '#0a6b0a', backgroundColor: status.error ? '#fdecea' : '#d9f5d9' }}
                >
                    {status.text}
                </p>
            ) : null}
        </>
    );
};

/** Filtro por un campo (juego, serie): vacio = todos. */
const FilterSelect = ({ options, value, onChange, placeholder }) => {
    const opts = options.map(o => ({ value: o, label: o }));
    return (
        <Select
            className='heroes selectForm'
            options={opts}
            isSearchable
            isClearable
            styles={selectStyles}
            placeholder={placeholder}
            value={opts.find(o => o.value === value) || null}
            onChange={(option) => onChange(option ? option.value : '')}
        />
    );
};

/** Guarda una celda editada (si ha cambiado) y recarga. */
const saveCell = async (type, row, field, value, onSaved) => {
    if (String(value).trim() === String(row[field] == null ? '' : row[field])) return;
    const res = await post('/api/adminLogin?action=edit&type=' + type, { id: row._id, field: field, value: value })
        .catch(() => ({ error: 'No se pudo guardar' }));
    if (!res.success) window.alert(res.error || 'No se pudo guardar');
    await onSaved();
};

// Rellenar pendientes: al confirmar una celda pendiente con Enter o Tab se salta a la siguiente
// pendiente. Primero las de su fila (a la derecha y, si no, las que quedaran a la izquierda),
// luego las filas de arriba, de la mas cercana a la mas lejana, y si arriba no queda nada, las
// de abajo. Devuelve el orden completo: tras guardar, alguna puede haberse rellenado sola.
const pendingPlan = (input) => {
    if (!input.classList.contains('missing')) return null;
    const rows = [...input.closest('tbody').querySelectorAll('tr')];
    const at = rows.indexOf(input.closest('tr'));
    const pending = (tr) => [...tr.querySelectorAll('input.missing')];
    const own = pending(rows[at]);
    const i = own.indexOf(input);
    return own.slice(i + 1).concat(own.slice(0, i), ...rows.slice(0, at).reverse().map(pending), ...rows.slice(at + 1).map(pending))
        .map(el => ({ row: el.dataset.row, field: el.dataset.field }));
};

// Tras guardar, la tabla se repinta: se va a la primera del plan que siga pendiente y se centra.
const focusPending = (plan) => window.requestAnimationFrame(() => {
    const next = plan
        .map(c => document.querySelector('.metricsTable input.missing[data-row="' + c.row + '"][data-field="' + c.field + '"]'))
        .find(Boolean);
    if (!next) return;
    next.focus({ preventScroll: true });
    next.scrollIntoView({ block: 'center', inline: 'center' });
});

// Plan de la celda que se esta confirmando con el teclado. Al salir con el raton no hay plan:
// no se le quita el foco a lo que se acaba de pulsar.
let pendingJump = null;

// La key incluye el valor: tras guardar y recargar, el input vuelve a leer lo del servidor.
const editCell = (type, row, field, onSaved, className = '', list) => (
    <input
        key={row._id + field + String(row[field])}
        className={className}
        list={list}
        data-row={row._id}
        data-field={field}
        defaultValue={row[field] == null ? '' : row[field]}
        onBlur={(e) => {
            const plan = pendingJump;
            pendingJump = null;
            saveCell(type, row, field, e.target.value, onSaved).then(() => plan && focusPending(plan));
        }}
        onKeyDown={(e) => {
            if (e.key !== 'Enter' && !(e.key === 'Tab' && !e.shiftKey)) return;
            pendingJump = pendingPlan(e.target);
            if (e.key === 'Tab' && !pendingJump) return; // fuera de las pendientes, el Tab de siempre
            e.preventDefault();
            e.target.blur();
        }}
    />
);

const deleteRow = async (type, row, label, onDone) => {
    if (!window.confirm('¿Borrar la fila de ' + label + '?')) return;
    await post('/api/adminLogin?action=delete&type=' + type, { id: row._id }).catch(() => null);
    await onDone();
};

// ---------------------------------------------------------------- DIRECTOS

// [campo, cabecera]. Los de datos se pueden corregir; los calculados no.
const STREAM_DATA_COLUMNS = [
    ['minutes', 'Min'], ['uniqueTwitch', 'Únicos TW'], ['avgTwitch', 'Media TW'], ['avgYoutube', 'Media YT'],
    ['marketViewers', 'Avg. Viewers'], ['marketStreamers', 'Streamers'], ['participation', 'Particip.']
];
const STREAM_SCORE_COLUMNS = [
    ['relativo', 'Relativo'], ['absoluto', 'Absoluto'], ['oportunidad', 'Oportunidad'], ['personal', 'Personal'],
    ['alineacion', 'Alineación'], ['total', 'Total'], ['alcance', 'Alcance'], ['eficiencia', 'Eficiencia'], ['ratioES', 'Ratio ES'],
    ['multiplier', 'Multi.']
];
const LINK_DAYS_MS = 30 * 24 * 60 * 60 * 1000; // un directo se enlaza a videos de su ultimo mes
const STREAM_EXTRA_COLUMNS = [['peakTwitch', 'Pico TW'], ['follows', 'Follows'], ['messages', 'Mensajes']];

const STREAM_TABS = [
    { id: 'resumen', label: 'Resumen por juego' },
    { id: 'directos', label: 'Directos' },
    { id: 'importar', label: 'Importar CSV' },
];

const StreamsPanel = () => {
    const [tab, setTab] = React.useState('resumen');
    const [game, setGame] = React.useState('');
    const [data, error, refresh] = useMetrics('streamMetrics');
    const [videos] = useMetrics('videoMetrics');

    const cell = (row, field, className = '') => editCell('streamMetrics', row, field, refresh,
        className + (field === 'participation' && row[field] == null ? ' missing' : ''));

    // Se enlaza la fila de YouTube: las de Instagram y TikTok del mismo video van con ella.
    const videoSelect = (row) => {
        const started = new Date(row.startedAt);
        const options = ((videos && videos.rows) || []).filter(v => v._id === row.video ||
            (v.platform === 'Youtube' && v.title && row.startedAt && new Date(v.date) <= started && started - new Date(v.date) <= LINK_DAYS_MS));
        return (
            <select key={row._id + String(row.video)} defaultValue={row.video || ''} style={{ maxWidth: 220 }}
                onChange={(e) => saveCell('streamMetrics', row, 'video', e.target.value, refresh)}>
                <option value=''>—</option>
                {options.map(v => <option key={v._id} value={v._id}>{fmtDay(v.date) + ' · ' + (v.title || v.series || '')}</option>)}
            </select>
        );
    };

    if (!data) return <p>{error || 'Cargando...'}</p>;

    const rows = (data.rows || []).filter(r => !game || r.game === game).reverse();
    const ranking = data.ranking || [];

    return (
        <>
            <Tabs tabs={STREAM_TABS} value={tab} onChange={setTab} />

            {tab === 'resumen' ? (
                <div className='metricsScroll'>
                    <table className='metricsTable'>
                        <thead>
                            <tr>
                                <th className='left'>Juego</th><th>N</th><th>Alcance</th><th>Eficiencia</th><th>Índice</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ranking.map(g => (
                                <tr key={g.game}>
                                    <td className='left'>{g.game}</td>
                                    <td>{g.n}</td>
                                    <td>{fmt(g.alcance, 4)}</td>
                                    <td>{fmt(g.eficiencia, 4)}</td>
                                    <td><strong>{fmt(g.indice, 1)}</strong></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {tab === 'directos' ? (
                <>
                    <p style={NOTE_STYLE}>
                        Cada directo de Twitch crea sus filas solo, una por categoría: minutos, media y pico de Twitch,
                        chatters únicos, follows y mensajes; la media de YouTube (los juegos de Pokémon cuentan en
                        NaruxForPoke); y de SullyGnome, la media de 30 días en español de la categoría (Avg. Viewers y Streamers).
                        Lo que hay que poner a mano: <strong>Participación</strong> (en amarillo si falta) y <strong>Tipo</strong>.
                        Si el directo viene de un vídeo, elígelo en <strong>Vídeo</strong>: su <strong>Multi.</strong> (media TW + YT
                        frente a un día normal del juego, sin vídeo = 1) pasa a ser el Multi. Stream del vídeo; con varios directos, la media.
                    </p>
                    <FilterSelect options={ranking.map(g => g.game)} value={game} onChange={setGame} placeholder='Todos los juegos' />
                    <Paged key={game} rows={rows} noun='directos'>
                        {pageRows => (
                            <div className='metricsScroll'>
                                <table className='metricsTable'>
                                    <thead>
                                        <tr>
                                            <th>Semana</th><th className='left'>Fecha</th><th className='left'>Juego</th><th className='left'>Tipo</th><th className='left'>Vídeo</th>
                                            {STREAM_DATA_COLUMNS.concat(STREAM_SCORE_COLUMNS, STREAM_EXTRA_COLUMNS).map(([field, label]) => <th key={field}>{label}</th>)}
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map(row => (
                                            <tr key={row._id}>
                                                <td>{cell(row, 'week')}</td>
                                                <td className='left'>{row.open ? '● en directo' : fmtDate(row.startedAt)}</td>
                                                <td className='left'>{cell(row, 'game', 'text')}</td>
                                                <td className='left'>{cell(row, 'type', 'text')}</td>
                                                <td className='left'>{videoSelect(row)}</td>
                                                {STREAM_DATA_COLUMNS.map(([field]) => <td key={field}>{cell(row, field)}</td>)}
                                                {STREAM_SCORE_COLUMNS.map(([field]) => <td key={field}>{fmt(row[field], 3)}</td>)}
                                                {STREAM_EXTRA_COLUMNS.map(([field]) => <td key={field}>{row.source === 'auto' ? fmt(row[field], 0) : ''}</td>)}
                                                <td><div className='miniBtn' onClick={() => deleteRow('streamMetrics', row, row.game + (row.type ? ' (' + row.type + ')' : ''), refresh)}>Borrar</div></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Paged>
                </>
            ) : null}

            {tab === 'importar' ? (
                <ImportCsv type='streamMetricsCsv' sheet='Juegos v2' extra='; las filas recogidas solas no se tocan' onDone={refresh} />
            ) : null}
        </>
    );
};

// ---------------------------------------------------------------- VIDEOS

// [campo, cabecera], en el orden de la hoja "Series V3".
const VIDEO_DATA_COLUMNS = [
    ['duration', 'Duración'], ['views', 'Views'], ['likes', 'Likes'], ['avgWatch', 'T.Medio /s'],
    ['follows', 'Follows'], ['comments', 'Comentarios'], ['saves', 'Guardados'], ['shares', 'Compartidos'],
    ['retencion', '% Retención'], ['stayed', '% Se han quedado'], ['feed', '% Feed'], ['search', '% Búsqueda'],
    ['prodMinutes', 'Minutos Prod'], ['multiStream', 'Multi. Stream']
];
const VIDEO_SCORE_COLUMNS = [
    ['pLike', 'P. Like/Views'], ['pComent', 'P. Coment/View'], ['pTMedio', 'P. T.Medio /s'], ['pFollow', 'P. Follow/View'],
    ['pGuardados', 'P. Guardados/V'], ['pCompartido', 'P. Compartido/V'], ['pDistribucion', 'P. Distribución'],
    ['pCalidad', 'P. Calidad'], ['pOutcomes', 'P. OutComes'], ['pFinal', 'P. Final'], ['pRoi', 'P. ROI'],
    ['pDecisor', 'P. Decisor'], ['primerVideo', 'Primer Vídeo'], ['videoAvgRoi', 'Vídeo AVG ROI'], ['pStay', 'P. Stay'],
    ['minProdAjustado', 'Min Prod Ajustado'], ['tiempoSerie', 'Tiempo Serie']
];

const VIDEO_PLATFORMS = ['Youtube', 'Instagram', 'TikTok'];
// Lo que se puede escribir a mano (la % Retencion sale del T.Medio y la duracion).
const VIDEO_EDITABLE = new Set(['duration', 'views', 'likes', 'avgWatch', 'follows', 'comments', 'saves', 'shares',
    'stayed', 'feed', 'search', 'prodMinutes', 'multiStream']);
const VIDEO_LABELS = Object.fromEntries([['series', 'Serie']].concat(VIDEO_DATA_COLUMNS));

const VIDEO_TABS = [
    { id: 'series', label: 'Series' },
    { id: 'longform', label: 'Long-form YouTube' },
    { id: 'videos', label: 'Vídeos' },
    { id: 'conexiones', label: 'Conexiones' },
    { id: 'importar', label: 'Importar CSV' },
];

/** De que cuentas se recogen los videos solos, y el enlace para conectar las de Instagram. */
/** Pegar el token que da el App Dashboard de Meta (Generate access tokens) para una cuenta. */
const PasteToken = ({ account, onSaved }) => {
    const [token, setToken] = React.useState('');
    const save = async () => {
        if (!token.trim()) return;
        const res = await post('/api/adminLogin?action=videoMetricsInstagramToken', { account: account, token: token })
            .catch(() => ({ error: 'No se pudo guardar' }));
        if (!res.success) return window.alert(res.error || 'No se pudo guardar');
        setToken('');
        window.alert('Token de @' + res.username + ' guardado.' + (account === 'NaruxForAll'
            ? (res.messaging ? ' El bot de mensajes también lo usará.' : ' No tiene permiso de mensajes: el bot sigue con el token de var.js.')
            : ''));
        await onSaved();
    };
    return (
        <span className='metricsForm' style={{ display: 'inline-flex', margin: '0 0 0 8px' }}>
            <input type='password' placeholder='Pega aquí el token' style={{ width: 180 }} value={token} onChange={(e) => setToken(e.target.value)}
                aria-label={'Token de Instagram de ' + account} />
            <span className='miniBtn' onClick={save}>Guardar</span>
        </span>
    );
};

const Connections = () => {
    const [data, error, refresh] = useMetrics('videoConnections');
    if (!data) return <p>{error || 'Cargando...'}</p>;
    const linkStyle = { color: 'white', textDecoration: 'none', marginLeft: 8 };
    return (
        <>
            <p style={NOTE_STYLE}>
                Instagram: pega en la fila de cada cuenta el token que te da Meta (App Dashboard › Instagram › API setup
                with Instagram login › Generate access tokens). Se comprueba que es de esa cuenta y luego se renueva solo.
                {data.instagramReady ? ' También puedes usar Conectar, que abre Instagram en una pestaña nueva.' : ''}
            </p>
            <p style={NOTE_STYLE}>
                TikTok: antes de pulsar Conectar en una cuenta, entra en tiktok.com con esa misma cuenta (TikTok autoriza
                la sesión que tengas abierta). Trae título, duración, views, likes, comentarios y compartidos; el resto, a mano.
            </p>
            <div className='metricsScroll'>
                <table className='metricsTable'>
                    <thead>
                        <tr><th className='left'>Plataforma</th><th className='left'>Cuenta</th><th className='left'>Estado</th></tr>
                    </thead>
                    <tbody>
                        {data.youtube.map(c => (
                            <tr key={'yt' + c.account}>
                                <td className='left'>YouTube</td>
                                <td className='left'>{c.account}</td>
                                <td className='left'>
                                    {!c.connected ? 'Sin token'
                                        : c.analytics ? 'Conectado, con estadísticas'
                                            : 'Conectado sin estadísticas: ejecuta "node getYtToken.js ' + c.tokenPath + '" con la sesión de ' + c.account}
                                </td>
                            </tr>
                        ))}
                        {data.instagram.map(c => (
                            <tr key={'ig' + c.account}>
                                <td className='left'>Instagram</td>
                                <td className='left'>{c.account}</td>
                                <td className='left'>
                                    {c.connected ? 'Conectado como @' + c.username + ' (se renueva solo; caduca el ' + fmtDate(c.expiresAt) + ')' : 'Sin conectar'}
                                    {/* El bot de mensajes es @naruxforall (apps/apps/instagram.js). */}
                                    {c.account === 'NaruxForAll' ? (c.messaging
                                        ? ' · el bot de mensajes usa este token'
                                        : ' · el bot de mensajes usa el token de var.js') : null}
                                    {data.instagramReady ? (
                                        <a className='miniBtn' style={linkStyle} target='_blank' rel='noopener noreferrer'
                                            href={'/login?instagramAdmin=1&account=' + encodeURIComponent(c.account)}>
                                            {c.connected ? 'Reconectar' : 'Conectar'}
                                        </a>
                                    ) : null}
                                    <PasteToken account={c.account} onSaved={refresh} />
                                </td>
                            </tr>
                        ))}
                        {(data.tiktok || []).map(c => (
                            <tr key={'tt' + c.account}>
                                <td className='left'>TikTok</td>
                                <td className='left'>{c.account}</td>
                                <td className='left'>
                                    {c.connected ? 'Conectado como "' + (c.displayName || '?') + '" (se renueva solo; hay que reconectar antes del ' + fmtDate(c.expiresAt) + ')' : 'Sin conectar'}
                                    {data.tiktokReady ? (
                                        <a className='miniBtn' style={linkStyle} target='_blank' rel='noopener noreferrer'
                                            href={'/login?tiktokAdmin=1&account=' + encodeURIComponent(c.account)}>
                                            {c.connected ? 'Reconectar' : 'Conectar'}
                                        </a>
                                    ) : ' · faltan tiktokClientKey y tiktokClientSecret en apps/var.js'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className='metricsPager'><div className='miniBtn' onClick={refresh}>Actualizar</div></div>
        </>
    );
};

/** Alta a mano de un video de una plataforma que aun no se recoge sola. */
const AddVideo = ({ channels, seriesList, onDone }) => {
    const [form, setForm] = React.useState({
        date: new Date().toLocaleDateString('sv-SE'), // AAAA-MM-DD en hora local
        channel: channels[0] || '',
        platform: 'TikTok',
        series: ''
    });
    const set = (key) => (e) => setForm(Object.assign({}, form, { [key]: e.target.value }));
    const add = async () => {
        const res = await post('/api/adminLogin?action=create&type=videoMetrics', form).catch(() => ({ error: 'No se pudo añadir' }));
        if (!res.success) return window.alert(res.error || 'No se pudo añadir');
        setForm(Object.assign({}, form, { series: '' }));
        await onDone();
    };
    return (
        <div className='metricsForm'>
            <input type='date' value={form.date} onChange={set('date')} aria-label='Fecha de publicación' />
            <select value={form.channel} onChange={set('channel')} aria-label='Canal'>
                {channels.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.platform} onChange={set('platform')} aria-label='Plataforma'>
                {VIDEO_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <input list={seriesList} placeholder='Serie' value={form.series} onChange={set('series')} aria-label='Serie' />
            <div className='miniBtn' onClick={add}>Añadir vídeo</div>
        </div>
    );
};

const VideosPanel = () => {
    const [tab, setTab] = React.useState('series');
    const [series, setSeries] = React.useState('');
    const [onlyPending, setOnlyPending] = React.useState(false);
    const [collectStatus, setCollectStatus] = React.useState('');
    const [data, error, refresh] = useMetrics('videoMetrics');

    const collect = async () => {
        setCollectStatus('Recogiendo...');
        try {
            const res = await post('/api/adminLogin?action=videoMetricsCollect', {});
            setCollectStatus(res.success
                ? res.results.map(r => r.account + ' (' + r.platform + '): ' + (r.error ? 'error: ' + r.error
                    : r.connected === false ? 'sin conectar'
                        : r.created + ' nuevos, ' + r.updated + ' actualizados' +
                          (r.analytics === false ? '; sin estadísticas: ' + (r.analyticsError || 'falta el permiso de YouTube Analytics') : ''))).join(' · ')
                : res.error || 'No se pudo recoger');
            await refresh();
        } catch (err) {
            setCollectStatus('No se pudo recoger');
        }
    };

    if (!data) return <p>{error || 'Cargando...'}</p>;

    const all = data.rows || [];
    const isPending = (r) => r.missing && r.missing.length > 0;
    // Lo mas reciente arriba; el mismo dia, lo ultimo que se dio de alta.
    const rows = all.filter(r => (!series || r.series === series) && (!onlyPending || isPending(r)))
        .reverse()
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    const seriesNames = [...new Set(all.map(r => r.series).filter(Boolean))].sort();
    const channels = [...new Set(all.map(r => r.channel).filter(Boolean))].sort();
    const ranking = data.series || [];
    const longForm = data.longForm || [];

    return (
        <>
            <Tabs tabs={VIDEO_TABS} value={tab} onChange={setTab} />

            {tab === 'series' ? (
                ranking.length === 0 ? <p>Aún no hay vídeos que pasen el umbral.</p> : (
                    <div className='metricsScroll'>
                        <table className='metricsTable'>
                            <thead>
                                <tr>
                                    <th className='left'>Serie</th><th>N Vídeos</th><th>ROI Promedio Vídeo</th><th>Última Fecha</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ranking.map(s => (
                                    <tr key={s.series}>
                                        <td className='left'>{s.series}</td>
                                        <td>{s.videos}</td>
                                        <td><strong>{fmt(s.roi, 1)}</strong></td>
                                        <td>{fmtDay(s.last)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : null}

            {tab === 'longform' ? (
                <>
                    <p style={NOTE_STYLE}>
                        Vídeos de YouTube con al menos 3000 views, un 10% de búsqueda y un 65% que se queda.
                        Score = % Búsqueda × 0,6 + % Retención × 0,4.
                    </p>
                    {longForm.length === 0 ? <p>Aún no hay vídeos que pasen el umbral.</p> : (
                        <div className='metricsScroll'>
                            <table className='metricsTable'>
                                <thead>
                                    <tr>
                                        <th className='left'>Serie</th><th>Views Prom YT</th><th>% Búsqueda</th><th>% Ret</th><th>N Iter</th><th>Score Long-Form</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {longForm.map(s => (
                                        <tr key={s.series}>
                                            <td className='left'>{s.series}</td>
                                            <td>{fmt(s.views, 0)}</td>
                                            <td>{fmt(s.search, 1)}</td>
                                            <td>{fmt(s.stayed, 1)}</td>
                                            <td>{s.n}</td>
                                            <td><strong>{fmt(s.score, 2)}</strong></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            ) : null}

            {tab === 'videos' ? (
                <>
                    <p style={NOTE_STYLE}>
                        YouTube, Instagram y TikTok (cuando conectes sus cuentas en Conexiones) se recogen solos cada día a las 06:00
                        durante la primera semana de cada vídeo; luego se quedan fijos, como cuando los apuntabas a mano. Si
                        alguno no entra solo, se da de alta aquí abajo con su fecha de publicación. En amarillo, lo que falta por poner a mano.
                    </p>
                    <div className='metricsPager'>
                        <div className='miniBtn' onClick={collect}>Recoger ahora</div>
                        <div className='miniBtn' onClick={() => setOnlyPending(!onlyPending)}>
                            {onlyPending ? '✓ ' : ''}Con datos pendientes ({all.filter(isPending).length})
                        </div>
                    </div>
                    {collectStatus ? <p style={NOTE_STYLE}>{collectStatus}</p> : null}
                    <AddVideo channels={channels.length ? channels : ['NaruxForAll', 'NaruxForPoke']} seriesList='videoSeries' onDone={refresh} />
                    <datalist id='videoSeries'>{seriesNames.map(s => <option key={s} value={s} />)}</datalist>
                    <FilterSelect options={seriesNames} value={series} onChange={setSeries} placeholder='Todas las series' />
                    <Paged key={series + onlyPending} rows={rows} noun='vídeos'>
                        {pageRows => (
                            <div className='metricsScroll'>
                                <table className='metricsTable'>
                                    <thead>
                                        <tr>
                                            <th className='left'>Fecha</th><th className='left'>Canal</th><th className='left'>Formato</th><th className='left stickyCol stickyPlatform'>Plataforma</th>
                                            <th className='left stickyCol stickyTitle'>Título</th><th className='left'>Serie</th><th className='left'>Faltan</th>
                                            {VIDEO_DATA_COLUMNS.concat(VIDEO_SCORE_COLUMNS).map(([field, label]) => <th key={field}>{label}</th>)}
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map(row => {
                                            const missing = new Set(row.missing || []);
                                            const mark = (field) => (missing.has(field) ? 'missing' : '');
                                            return (
                                                <tr key={row._id}>
                                                    <td className='left'>{fmtDay(row.date)}</td>
                                                    <td className='left'>{row.channel}</td>
                                                    <td className='left'>{row.format}</td>
                                                    <td className='left stickyCol stickyPlatform'>{row.platform}</td>
                                                    {/* Las filas del historico no tienen titulo: se reconocen por la serie. */}
                                                    <td className='left stickyCol stickyTitle' title={row.title}>{(row.title || row.series || '').slice(0, 30)}</td>
                                                    <td className='left'>{editCell('videoMetrics', row, 'series', refresh, 'text ' + mark('series'), 'videoSeries')}</td>
                                                    <td className='left pending'>{[...missing].map(f => VIDEO_LABELS[f]).join(', ')}</td>
                                                    {VIDEO_DATA_COLUMNS.map(([field]) => (
                                                        <td key={field}>
                                                            {field === 'multiStream' && row.multiStreamLinks
                                                                ? <span title={'Media de ' + row.multiStreamLinks + ' directo(s) enlazado(s)'}>{fmt(row[field], 2)} <span role='img' aria-label='de directos'>🔗</span></span>
                                                                // Los 3 primeros de la serie se pueden corregir (lo escrito); el resto usa su media.
                                                                : field === 'prodMinutes' && row.prodSeries
                                                                ? <span title={'Media de la serie (' + row.prodSeries.map(v => fmt(v, 2)).join(' · ') + '): ' + fmt(row.prodMinutes, 2)}>
                                                                    {row.prodTyped != null ? editCell('videoMetrics', Object.assign({}, row, { prodMinutes: row.prodTyped }), field, refresh) : fmt(row[field], 2)}
                                                                    {' '}<span role='img' aria-label='media de la serie'>🔗</span></span>
                                                                : VIDEO_EDITABLE.has(field) ? editCell('videoMetrics', row, field, refresh, mark(field)) : fmt(row[field], 2)}
                                                        </td>
                                                    ))}
                                                    {VIDEO_SCORE_COLUMNS.map(([field]) => <td key={field}>{fmt(row[field], 2)}</td>)}
                                                    <td><div className='miniBtn' onClick={() => deleteRow('videoMetrics', row, row.series || row.title || 'este vídeo', refresh)}>Borrar</div></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Paged>
                </>
            ) : null}

            {tab === 'conexiones' ? <Connections /> : null}

            {tab === 'importar' ? <ImportCsv type='videoMetricsCsv' sheet='Series V3' extra='' onDone={refresh} /> : null}
        </>
    );
};

// ---------------------------------------------------------------- ARENA

const ARENA_TABS = [
    { id: 'combates', label: 'Combates por día' },
    { id: 'niveles', label: 'Top niveles' },
    { id: 'stats', label: 'Stats' },
    { id: 'winrate', label: 'Winrate' },
];
const STAT_LABEL = { ataque: 'Ataque', agilidad: 'Agilidad', velocidad: 'Velocidad', equilibrado: 'Equilibrado' };
const fmtPct = (v) => (v === null || v === undefined ? '—' : fmt(v, 1) + ' %');

/** Lo calcula apps/arena/arena.js (metricas) con las stats con las que se lucho cada combate. */
const ArenaPanel = () => {
    const [tab, setTab] = React.useState('combates');
    const [data, error] = useMetrics('arenaMetrics');
    if (!data) return <p>{error || 'Cargando...'}</p>;
    if (data.error) return <p>{data.error}</p>;

    const statTable = (rows, cols) => (
        <div className='metricsScroll'>
            <table className='metricsTable'>
                <thead><tr><th className='left'>Stat</th>{cols.map(([, label]) => <th key={label}>{label}</th>)}</tr></thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.stat}>
                            <td className='left'>{STAT_LABEL[r.stat]}</td>
                            {cols.map(([render, label]) => <td key={label}>{render(r)}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <>
            <Tabs tabs={ARENA_TABS} value={tab} onChange={setTab} />

            {tab === 'combates' ? (
                <div className='metricsScroll'>
                    <table className='metricsTable'>
                        <thead>
                            <tr><th className='left'>Día</th><th>Combates</th><th>Contra personas</th><th>Contra bots</th><th>Personas que lanzan</th></tr>
                        </thead>
                        <tbody>
                            {data.dias.map(d => (
                                <tr key={d.dia}>
                                    <td className='left'>{d.dia}</td>
                                    <td><strong>{d.total}</strong></td>
                                    <td>{d.personas}</td>
                                    <td>{d.bots}</td>
                                    <td>{d.luchadores}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.dias.length ? null : <p style={NOTE_STYLE}>Aún no hay combates.</p>}
                </div>
            ) : null}

            {tab === 'niveles' ? (
                <div className='metricsScroll'>
                    <table className='metricsTable'>
                        <thead>
                            <tr><th>#</th><th className='left'>Luchador</th><th>Nivel</th><th>XP</th><th>Ataque</th><th>Agilidad</th><th>Velocidad</th></tr>
                        </thead>
                        <tbody>
                            {data.top.map((u, i) => (
                                <tr key={u.id}>
                                    <td>{i + 1}</td>
                                    <td className='left'>{u.name}</td>
                                    <td><strong>{u.nivel}</strong></td>
                                    <td>{u.experiencia}</td>
                                    <td>{u.ataque}</td>
                                    <td>{u.agilidad}</td>
                                    <td>{u.velocidad}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {tab === 'stats' ? (
                <>
                    <p style={NOTE_STYLE}>
                        Puntos elegidos al subir de nivel, de {data.cuentasConPuntos} cuentas. Al subir de tier se borran los de esa cuenta.
                    </p>
                    {statTable(data.puntos, [[r => r.puntos, 'Puntos'], [r => <strong>{fmtPct(r.pct)}</strong>, '%']])}
                </>
            ) : null}

            {tab === 'winrate' ? (
                <>
                    <p style={NOTE_STYLE}>
                        <strong>Quien tiene más:</strong> de los combates en que los dos la tienen distinta, % que gana el que tiene más.
                    </p>
                    {statTable(data.masStat, [[r => r.combates, 'Combates'], [r => <strong>{fmtPct(r.winrate)}</strong>, 'Gana el que tiene más']])}
                    <p style={NOTE_STYLE}>
                        <strong>Por stat principal</strong> (la más alta de cada luchador, bots incluidos; Equilibrado = empate arriba):
                        % de victorias de los luchadores de cada tipo.
                    </p>
                    {statTable(data.porPrincipal, [[r => r.combates, 'Luchadores'], [r => <strong>{fmtPct(r.winrate)}</strong>, 'Winrate']])}
                </>
            ) : null}
        </>
    );
};

// ---------------------------------------------------------------- PANEL

const AREAS = [
    { id: 'directos', label: 'Directos' },
    { id: 'videos', label: 'Vídeos' },
    { id: 'arena', label: 'Arena' },
];

const AdminMetricas = () => {
    const [area, setArea] = React.useState('directos');
    return (
        <div className='heroes formOver'>
            <h1>Métricas</h1>
            <Tabs tabs={AREAS} value={area} onChange={setArea} />
            {area === 'directos' ? <StreamsPanel /> : area === 'videos' ? <VideosPanel /> : <ArenaPanel />}
        </div>
    );
};

export default AdminMetricas;
